import React, { useState, useEffect } from 'react'
import { Modal } from 'components/ui/Modal'
import { Button } from 'components/ui/Button'
import { Badge } from 'components/ui/Badge'
import { Loader2 } from 'lucide-react'
import {
  CheckCircle,
  XCircle,
  Copy,
  FileText
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Notification } from 'contexts/NotificationContext'
import { uploadsApi } from 'lib/api'

interface BatchDetailsModalProps {
  isOpen: boolean
  onClose: () => void
  notification: Notification | null
}

export function BatchDetailsModal({
  isOpen,
  onClose,
  notification
}: BatchDetailsModalProps) {
  const { t } = useTranslation()
  const [batchStatus, setBatchStatus] = useState<any>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    // Get batchId from notification (could be in batchId, metadata.batchId, or metadata.batch_id)
    const batchId = notification?.batchId || notification?.metadata?.batchId || notification?.metadata?.batch_id
    
    if (isOpen && batchId) {
      setLoading(true)
      setError(null)
      uploadsApi.getBatchStatus(batchId)
        .then((response) => {
          setBatchStatus(response.data)
          setLoading(false)
        })
        .catch((err) => {
          console.error('Error fetching batch status:', err)
          setError(err.response?.data?.message || t('candidates.batchDetailsModal.fetchError', 'Failed to fetch batch details'))
          setLoading(false)
        })
    }
  }, [isOpen, notification?.batchId, t])

  // Get batchId from notification (could be in batchId, metadata.batchId, or metadata.batch_id)
  const batchId = notification?.batchId || notification?.metadata?.batchId || notification?.metadata?.batch_id
  
  if (!batchId) {
    return null
  }

  if (loading) {
    return (
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title={t('candidates.batchDetailsModal.title', 'Batch Import Details')}
        size="xl"
      >
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </Modal>
    )
  }

  if (error || !batchStatus) {
    return (
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title={t('candidates.batchDetailsModal.title', 'Batch Import Details')}
        size="xl"
      >
        <div className="text-center py-12">
          <p className="text-red-600 dark:text-red-400">
            {error || t('candidates.batchDetailsModal.notFound', 'Batch details not found')}
          </p>
        </div>
      </Modal>
    )
  }

  const allFiles = [
    ...(batchStatus.files || []).map(f => ({ ...f, type: 'processed' as const })),
    ...(batchStatus.skipped_files || []).map(f => ({ ...f, type: 'skipped' as const }))
  ].sort((a, b) => (a.file_index || 0) - (b.file_index || 0))

  const getStatusIcon = (status: string, type?: 'skipped' | 'processed') => {
    const baseClasses = "h-4 w-4"
    
    if (type === 'skipped') {
      return <Copy className={`${baseClasses} text-orange-500`} />
    }
    
    const statusLower = status?.toLowerCase()
    switch (statusLower) {
      case 'processed':
      case 'completed':
        return <CheckCircle className={`${baseClasses} text-green-500`} />
      case 'failed':
        return <XCircle className={`${baseClasses} text-red-500`} />
      default:
        return <FileText className={`${baseClasses} text-gray-500`} />
    }
  }

  const getStatusLabel = (status: string) => {
    const statusLower = status?.toLowerCase()
    const statusKey = `candidates.massImportModal.status${statusLower.charAt(0).toUpperCase() + statusLower.slice(1)}`
    return t(statusKey, status)
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('candidates.batchDetailsModal.title', 'Batch Import Details')}
      size="xl"
    >
      <div className="space-y-6">
        {/* Summary Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 border border-blue-200 dark:border-blue-800">
            <div className="text-sm text-blue-600 dark:text-blue-400 font-medium mb-1">
              {t('candidates.batchDetailsModal.total', 'Total')}
            </div>
            <div className="text-2xl font-bold text-blue-900 dark:text-blue-100">
              {batchStatus.total_files}
            </div>
          </div>
          <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 border border-blue-200 dark:border-blue-800">
            <div className="text-sm text-blue-600 dark:text-blue-400 font-medium mb-1">
              {t('candidates.batchDetailsModal.processed', 'Processed')}
            </div>
            <div className="text-2xl font-bold text-blue-900 dark:text-blue-100">
              {batchStatus.processed_files}
            </div>
          </div>
          <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4 border border-green-200 dark:border-green-800">
            <div className="text-sm text-green-600 dark:text-green-400 font-medium mb-1">
              {t('candidates.batchDetailsModal.successful', 'Successful')}
            </div>
            <div className="text-2xl font-bold text-green-900 dark:text-green-100">
              {batchStatus.successful_files || 0}
            </div>
          </div>
          <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-4 border border-red-200 dark:border-red-800">
            <div className="text-sm text-red-600 dark:text-red-400 font-medium mb-1">
              {t('candidates.batchDetailsModal.failed', 'Failed')}
            </div>
            <div className="text-2xl font-bold text-red-900 dark:text-red-100">
              {batchStatus.failed_files || 0}
            </div>
          </div>
          <div className="bg-orange-50 dark:bg-orange-900/20 rounded-lg p-4 border border-orange-200 dark:border-orange-800">
            <div className="text-sm text-orange-600 dark:text-orange-400 font-medium mb-1">
              {t('candidates.batchDetailsModal.skipped', 'Skipped')}
            </div>
            <div className="text-2xl font-bold text-orange-900 dark:text-orange-100">
              {batchStatus.skipped_files?.length || 0}
            </div>
          </div>
        </div>

        {/* Batch Date */}
        {batchStatus.completed_at && (
          <div className="bg-muted/50 rounded-lg p-4">
            <div className="text-sm">
              <span className="text-muted-foreground font-medium">
                {t('candidates.batchDetailsModal.completedAt', 'Completed at')}:
              </span>
              <span className="ml-2">
                {new Date(batchStatus.completed_at).toLocaleString()}
              </span>
            </div>
          </div>
        )}

        {/* Files List */}
        {allFiles.length > 0 && (
          <div>
            <h3 className="text-sm font-semibold mb-3">
              {t('candidates.batchDetailsModal.filesList', 'Files')} ({allFiles.length})
            </h3>
            <div className="border rounded-lg divide-y divide-border max-h-96 overflow-y-auto">
              {allFiles.map((file, idx) => {
                const isSkipped = file.type === 'skipped'
                const isFailed = file.status === 'failed' || (file.type === 'processed' && 'error_message' in file && file.error_message)
                const skippedFile = isSkipped ? (batchStatus.skipped_files || []).find(sf => sf.file_index === file.file_index) : null

                return (
                  <div
                    key={idx}
                    className={`p-3 text-sm ${
                      isSkipped
                        ? 'bg-orange-50 dark:bg-orange-950/20 border-l-2 border-orange-500'
                        : isFailed
                        ? 'bg-red-50 dark:bg-red-950/20 border-l-2 border-red-500'
                        : 'hover:bg-muted/50'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      {getStatusIcon(file.status || '', file.type)}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <p className="font-medium truncate">{file.filename}</p>
                          {file.status && file.type === 'processed' && (
                            <Badge
                              variant={
                                file.status === 'processed' || file.status === 'completed'
                                  ? 'default'
                                  : file.status === 'failed'
                                  ? 'destructive'
                                  : 'secondary'
                              }
                              className="text-xs"
                            >
                              {getStatusLabel(file.status)}
                            </Badge>
                          )}
                        </div>
                        {isSkipped && skippedFile && (
                          <p className="text-xs text-orange-600 dark:text-orange-400 mt-1">
                            {t('candidates.massImportModal.duplicateFileMessage', 'This CV file has already been uploaded previously.')}
                            {skippedFile.duplicate_candidate_name && (
                              <span className="ml-1">
                                ({t('candidates.massImportModal.alreadyExistsFor', 'Already exists for')}: {skippedFile.duplicate_candidate_name})
                              </span>
                            )}
                          </p>
                        )}
                        {isFailed && 'error_message' in file && file.error_message && (
                          <p className="text-xs text-red-600 dark:text-red-400 mt-1 break-words">
                            {file.error_message}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-2 pt-4 border-t">
          <Button variant="outline" onClick={onClose}>
            {t('candidates.massImportModal.close', 'Close')}
          </Button>
        </div>
      </div>
    </Modal>
  )
}

